import React, { useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { getAllOrders } from '../firebase/orders';
import { TableSkeleton } from './LoadingSkeleton';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';

export function AdminOrdersTab({ orders, setOrders, showToast, loading, setLoading }) {
  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const allOrders = await getAllOrders();
      setOrders(allOrders);
    } catch (error) {
      showToast('Error loading orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Orders</h2>
        <HelpTooltip
          title="Order Management"
          content="View and manage all customer orders here. See order details, payment status, customer information, and items purchased. Orders are created when customers complete checkout. Track fulfillment and manage the entire order lifecycle."
        />
      </div>

      <FeatureGuide
        title="Order Management Guide"
        description="Orders are created automatically when customers complete checkout. View all orders, see payment status, track fulfillment, and manage customer purchases. Orders include customer details, purchased items, totals, and payment information."
        steps={[
          "Orders appear here automatically when customers complete checkout",
          "View order details: customer name, email, items, total, and status",
          "Payment status shows: pending (awaiting payment), completed (paid), failed, or refunded",
          "Order status shows: pending, processing, completed, or cancelled",
          "Click on an order to see full details (coming soon)",
          "Use orders to fulfill digital products and track revenue"
        ]}
        tips={[
          "Completed orders with 'completed' payment status are fully paid",
          "Pending orders may need payment verification",
          "Failed orders indicate payment issues - contact customer if needed",
          "Digital products are delivered automatically after payment",
          "Export orders to CSV for accounting or record-keeping (coming soon)",
          "Monitor orders regularly to ensure timely fulfillment"
        ]}
      />

      <InfoBanner type="info" title="Order Statuses Explained">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li><strong>Pending:</strong> Order created but payment not yet completed</li>
          <li><strong>Processing:</strong> Payment received, order being fulfilled</li>
          <li><strong>Completed:</strong> Order fully processed and delivered</li>
          <li><strong>Cancelled:</strong> Order was cancelled</li>
          <li><strong>Payment Status:</strong> Shows if payment was successful (completed), still pending, failed, or refunded</li>
        </ul>
      </InfoBanner>
      {loading ? (
        <TableSkeleton rows={5} />
      ) : orders.length === 0 ? (
        <div className="bg-gradient-to-br from-[#fae1e5]/20 to-[#f3e6d5]/20 rounded-2xl border border-[#eaddcf] p-16 text-center">
          <div className="w-20 h-20 bg-[#fae1e5] rounded-full flex items-center justify-center mx-auto mb-6">
            <TrendingUp className="w-10 h-10 text-[#d48c95]" />
          </div>
          <p className="text-lg text-gray-600 font-medium">No orders yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {orders.map(order => (
            <div
              key={order.id}
              className="bg-gradient-to-br from-white to-[#fdfbf7] rounded-2xl border border-[#eaddcf] p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-[#c5a059]/30"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Left Section - Order Info */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#fae1e5] to-[#f3e6d5] flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-serif text-[#c5a059] font-semibold">
                        #
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-serif text-gray-800 mb-1">
                        Order #{order.id.slice(0, 8)}
                      </h3>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-700 font-medium">{order.customerName}</div>
                        <div className="text-sm text-gray-500">{order.customerEmail}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section - Order Details */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex flex-col items-end gap-2">
                      {order.items && order.items.map((item, index) => (
                        <span key={index} className="px-3 py-1.5 text-sm font-medium rounded-full bg-[#f3e6d5] text-[#c5a059] border border-[#c5a059]/20 text-right max-w-[200px] truncate" title={item.productTitle || item.name}>
                          {item.productTitle || item.name || 'Item'}
                          {item.quantity > 1 && ` (x${item.quantity})`}
                        </span>
                      ))}
                    </div>
                    <span className="text-lg font-serif text-[#c5a059] font-semibold">
                      £{order.total?.toFixed(2) || '0.00'}
                    </span>
                    <span className={`px-3 py-1.5 text-xs font-medium rounded-full ${order.paymentStatus === 'completed'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : order.paymentStatus === 'pending'
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                      {order.paymentStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

